import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import Seo from '../../components/Seo';
import {
  Landmark, RefreshCw, ShieldCheck, GraduationCap, Scale, Users, FileSignature,
  RotateCw, BadgeCheck, Bell, Search, MapPin, ArrowRight, Briefcase,
} from 'lucide-react';

const WHAT_YOULL_FIND = [
  { Icon: Landmark, text: 'Official Licensing Board Link' },
  { Icon: FileSignature, text: 'Application & Renewal Links' },
  { Icon: GraduationCap, text: 'Continuing Education Requirements' },
  { Icon: Scale, text: 'Ethics Requirements' },
  { Icon: Users, text: 'Clinical Supervision Requirements' },
  { Icon: FileSignature, text: 'ASWB Exam Information' },
  { Icon: RotateCw, text: 'Reciprocity & Endorsement Information' },
  { Icon: Bell, text: 'Renewal Reminders for Members' },
  { Icon: ShieldCheck, text: 'License Verification Link' },
];

const QUICK = [
  { Icon: FileSignature, title: 'APPLY', sub: 'for a License' },
  { Icon: RefreshCw, title: 'RENEW', sub: 'Your License' },
  { Icon: BadgeCheck, title: 'VERIFY', sub: 'a License' },
  { Icon: Landmark, title: 'VISIT', sub: 'Licensing Board' },
];

const LicenseCenter = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [states, setStates] = useState([]);
  const [selected, setSelected] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/license-center/states`).then((r) => setStates(r.data.states || [])).catch(() => {});
  }, []);

  const go = (slug) => { if (slug) navigate(`/license-renewal-center/${slug}`); };
  const filtered = states.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()) || s.abbr.toLowerCase() === query.toLowerCase());

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <Seo
        exactTitle="Social Work Licensing & Renewal by State | INASW"
        description="Social work licensing and renewal information for every U.S. state and Washington, D.C. — licensing boards, application and renewal links, CE and ethics requirements, supervision, ASWB exam info and reciprocity."
        path="/license-renewal-center"
      />
      {/* Hero */}
      <section className="relative bg-primary pt-24 pb-14" data-testid="license-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="text-secondary font-semibold tracking-widest uppercase text-xs mb-2">Introducing the New</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">National Social Work <span className="text-secondary">License &amp; Renewal</span> Center<sup className="text-secondary text-2xl align-super">™</sup></h1>
          <p className="mt-4 text-white/90 text-lg max-w-2xl">Your one-stop resource for social work licensing information in every state and Washington, D.C.</p>
          <p className="mt-2 text-secondary font-semibold">Every State. Every Resource. All in One Place.</p>

          {/* State selector */}
          <div className="mt-6 bg-white rounded-xl p-4 max-w-2xl">
            <p className="text-primary font-semibold text-sm mb-2">Find Social Work Licensing Information in Your State</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <select className="flex-1 border rounded-md h-11 px-3 text-sm text-gray-900 bg-white" value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="license-state-select">
                <option value="">Select Your State</option>
                {states.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
              <Button onClick={() => go(selected)} disabled={!selected} className="bg-primary hover:bg-primary/90 text-white h-11 px-6" data-testid="license-search-btn"><Search className="w-4 h-4 mr-2" /> Search</Button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {QUICK.map((q) => (
              <div key={q.title} className="bg-white/10 rounded-xl p-3 text-center">
                <q.Icon className="w-6 h-6 text-secondary mx-auto" />
                <p className="font-bold text-sm mt-1">{q.title}</p>
                <p className="text-xs text-white/70">{q.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you'll find */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="font-serif text-2xl font-bold text-primary mb-4">What You'll Find on Every State Page</h2>
          <div className="space-y-2">
            {WHAT_YOULL_FIND.map((f) => (
              <div key={f.text} className="flex items-center gap-3 bg-white rounded-xl border p-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><f.Icon className="w-4 h-4 text-primary" /></div>
                <p className="text-sm text-gray-700 font-medium">{f.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-24">
          <img src="/license-renewal-flyer.png" alt="National License & Renewal Center — every state, every resource, all in one place"
            className="w-full rounded-2xl shadow-lg border border-primary/10" loading="lazy" data-testid="license-flyer" />
        </div>
      </section>

      {/* Renewal reminders */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="bg-primary rounded-2xl p-6 sm:p-8 text-white grid md:grid-cols-3 gap-6 items-center">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center"><Bell className="w-8 h-8 text-secondary" /></div>
            <p className="font-semibold mt-2">Renewal Reminders for Members</p>
          </div>
          <div className="md:col-span-2">
            <h2 className="font-serif text-2xl font-bold">Never miss a renewal deadline again!</h2>
            <p className="text-white/80 mt-2">Logged-in members can add their license information and receive reminders at <strong>90</strong>, <strong>60</strong>, <strong>30</strong>, and <strong>7</strong> days before expiration.</p>
            <Button onClick={() => navigate(isAuthenticated ? '/my-license-reminders' : '/login')} className="mt-4 bg-secondary hover:bg-secondary-hover text-white" data-testid="setup-reminders-btn">
              <Bell className="w-4 h-4 mr-2" /> {isAuthenticated ? 'Set Up Renewal Reminders' : 'Sign In to Set Reminders'}
            </Button>
          </div>
        </div>
      </section>

      {/* Browse states */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-serif text-2xl font-bold text-primary">Browse States <span className="text-gray-400 text-base font-normal">(50 States + D.C.)</span></h2>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9" placeholder="Filter states" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="license-filter-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((s) => (
            <Link key={s.slug} to={`/license-renewal-center/${s.slug}`} data-testid={`state-card-${s.slug}`}
              className="flex items-center justify-between gap-2 bg-white rounded-xl border p-4 hover:border-secondary hover:shadow-sm transition-all group">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center">{s.abbr}</span>
                <span className="text-sm font-medium text-gray-700">{s.name}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-secondary" />
            </Link>
          ))}
        </div>
        <div className="mt-8 bg-secondary/10 border border-secondary/30 rounded-xl p-4 flex gap-3 text-sm text-primary">
          <MapPin className="w-5 h-5 flex-shrink-0 text-secondary" />
          <p>Licensing requirements may change. Always confirm current information with the official state licensing board. Each state page also links you to real social work <strong>jobs</strong>, <strong>clinical supervisors</strong>, and <strong>internships</strong> in that state.</p>
        </div>
      </section>
    </div>
  );
};

export default LicenseCenter;
